import Add from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import { IconButton, Paper, Switch } from "@mui/material";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import { format, fromUnixTime } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { confirmAction, showError, showMessage } from "../../actions/uiActions";
import DomainApi from "../../api/domains";
import PaginatedTable from "../../components/PaginatedTable";
import ConfigurationModal from "./EditConfigurationModal";

import { switchStyle } from "../../utils/styles";

const DomainSsoConfiguration = () => {
  const dispatch = useDispatch();
  const { domainId } = useParams();

  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [domain, setDomain] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("edit");

  const getConfigurations = useCallback(
    async (pageNum) => {
      try {
        const result = await DomainApi.getSsoConfigurations(
          domainId,
          pageNum || 0,
        );
        if (result) {
          setLoading(false);
          setPage(result.page);
          setCount(result.count);
          setConfigurations(result.ssoConfigurations);
        }
      } catch (error) {
        setLoading(false);
        dispatch(showError());
      }
    },
    [domainId, dispatch],
  );

  const getDomain = useCallback(async () => {
    try {
      const result = await DomainApi.get(domainId);
      if (result) {
        setLoading(false);
        setDomain(result);
      }
    } catch (error) {
      setLoading(false);
      dispatch(showError());
    }
  }, [domainId, dispatch]);

  const handleToggleStatus = async (id, currentStatus) => {
    const updateStatus = async () => {
      try {
        await DomainApi.updateSsoConfiguration(id, { status: !currentStatus });
        setConfigurations((prev) =>
          prev.map((config) =>
            config._id === id ? { ...config, status: !currentStatus } : config,
          ),
        );
        dispatch(
          showMessage(
            currentStatus ? "SSO has been disabled" : "SSO has been enabled",
          ),
        );
      } catch (error) {
        dispatch(showError(error));
      }
    };

    if (currentStatus) {
      // Currently enabled, show confirmation before disabling
      dispatch(
        confirmAction(
          "You are going to disable SSO for this domain. Users will no longer be able to log in via SSO. Do you want to proceed?",
          updateStatus,
        ),
      );
    } else {
      // Currently disabled, enable without confirmation
      await updateStatus();
    }
  };

  const handleRowClick = (config) => {
    setSelectedConfig(config);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedConfig(null);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedConfig(null);
    setModalMode("edit");
  };

  const handleSaveSuccess = (savedConfig, action) => {
    if (action === "edit") {
      setConfigurations((prev) =>
        prev.map((config) =>
          config._id === savedConfig._id ? savedConfig : config,
        ),
      );
    } else if (action === "create" && savedConfig) {
      setConfigurations((prev) => [savedConfig, ...prev]);
      setCount((prev) => prev + 1);
    }
    handleCloseModal();
  };

  useEffect(() => {
    getDomain();
    getConfigurations();
  }, [getDomain, getConfigurations]);

  return (
    <div>
      <section className="animated fadeIn">
        <header>
          <div>
            <h1>SSO configuration</h1>
            <h4 style={{ textAlign: "left" }}>{domain?.name}</h4>
          </div>

          <Tooltip
            title="Add SSO Configuration"
            placement="top"
            className="floating-button"
          >
            <Fab
              onClick={handleAddClick}
              variant="round"
              color="primary"
              aria-label="Add"
            >
              <Add />
            </Fab>
          </Tooltip>
        </header>
        <div className="line-break"></div>
        {/* Add content here */}

        <Paper style={{ overflowX: "auto" }}>
          <PaginatedTable
            goToPage={getConfigurations}
            count={count}
            page={page}
            loading={loading}
            rows={configurations.map((config) => ({
              properties: {
                key: config._id,
              },
              columns: [
                {
                  properties: {
                    key: `${config._id}-1`,
                  },
                  value: config.entityId,
                },
                {
                  properties: {
                    key: `${config._id}-2`,
                  },
                  value: config.emailDomain.join(", "),
                },
                {
                  properties: {
                    key: `${config._id}-3`,
                  },
                  value: format(fromUnixTime(config.createdAt), "MM-dd-yyyy"),
                },
                {
                  properties: {
                    key: `${config._id}-4`,
                  },
                  value: (
                    <Tooltip
                      title={
                        !config.status
                          ? "Users for this domain will log in with standard email/password (no SSO)."
                          : ""
                      }
                      placement="top"
                    >
                      <Switch
                        checked={config.status}
                        onChange={() =>
                          handleToggleStatus(config._id, config.status)
                        }
                        color="primary"
                        size="small"
                        sx={switchStyle}
                      />
                    </Tooltip>
                  ),
                },
                {
                  properties: {
                    key: `${config._id}-5`,
                  },
                  value: (
                    <Tooltip title="Edit configuration">
                      <IconButton
                        onClick={() => handleRowClick(config)}
                        color="primary"
                        size="small"
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  ),
                },
              ],
            }))}
            emptyMessage="No SSO configurations found"
            columns={[
              {
                name: "Entity ID",
                properties: {
                  key: 1,
                  className: "width30pct",
                },
              },
              {
                name: "Email Domains",
                properties: {
                  key: 2,
                  className: "width30pct",
                },
              },
              {
                name: "Created At",
                properties: {
                  key: 3,
                  className: "width20pct",
                },
              },
              {
                name: "Status",
                properties: {
                  key: 4,
                  className: "width15pct",
                },
              },
              {
                name: "Actions",
                properties: {
                  key: 5,
                  className: "width10pct",
                },
              },
            ]}
          />
        </Paper>

        {isModalOpen && (
          <ConfigurationModal
            open={isModalOpen}
            onClose={handleCloseModal}
            onSaveSuccess={handleSaveSuccess}
            configuration={selectedConfig}
            domainId={domainId}
            mode={modalMode}
          />
        )}
      </section>
    </div>
  );
};

export default DomainSsoConfiguration;
